import React, { useEffect, useMemo, useState } from "react";

import {
    MdChevronRight,
    MdExpandMore,
    MdOutlineCheckCircle,
    MdLockOutline,
    // MdLogout, // Temporarily removed with the Sign Out button (see commented footer below)
} from "react-icons/md";

import { Home3 } from "iconsax-reactjs";

import { Link, useLocation } from "react-router-dom";

import { useGlobal } from "../context/GlobalContext";
import { useEnrollmentSteps } from "../context/EnrollmentStepsContext";

import { dataApi, interviewsApi } from "@/services/api";
import { PAGE_IDS } from "@/core/constants/pageIds";
import { useDynamicTileStatuses, useEnrollmentInformationStatus } from "@/hooks/useDynamicTileStatuses";

interface Step
{
    id: string;
    title: string;
    path: string;
    /** The step the applicant is on, when the page says so (otherwise it is matched on the URL's `?step=`). */
    current?: boolean;
    /** Not reachable yet: shown, but not a link. */
    disabled?: boolean;
    /** Saved as complete (the applicant's completed pages): shown with a check mark. */
    completed?: boolean;
    /** Holds saved data but is not complete (the student moved on, for example without signing the transcript request): shown in amber. */
    incomplete?: boolean;
}

interface MenuItem {
    path: string;
    label: string;
    icon: React.ComponentType<any> | null;
    submenu?: Step[];
    badge?: number;
    extraClass?: string;
    enabled: boolean;
    /** Shown on hover or focus of a locked row, so the applicant sees why it is not available (UAT SEP-119). */
    lockedReason?: string;
    isCurrent: boolean;
}

interface DashboardSidebarProps {
    isOpen?: boolean;
    onClose?: () => void;
    profileComplete: boolean;
}

interface SidebarEntryProps
{
    item: MenuItem;
    onClose?: () => void;
}

/** The `?step=` value a submenu link points at (registry step ids are not always the URL values). */
const stepKeyOf = (path: string): string | null =>
    new URLSearchParams(path.split("?")[1] ?? "").get("step");

interface SidebarSubmenuProps
{
    items: Step[];
    currentStep: string;
    onClose?: () => void;
}

/**
 * The substeps under a menu item (Personal Profile and Enrollment Information). The stakeholder UI update removed
 * the collapsible submenu from every section; it is restored for these two, so an applicant on the route can see
 * and move between its steps. A step with `disabled` set (not reached yet) is listed but is not a link.
 */
/** The step's status circle: a green check when saved complete, a dashed circle otherwise. */
const StepStatusIcon: React.FC<{ completed?: boolean; locked?: boolean; incomplete?: boolean }> = ({ completed, locked, incomplete }) =>
    locked ? (
        <>
            <span className="flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden="true"><MdLockOutline className="h-3.5 w-3.5 text-slate-500" /></span>
            <span className="sr-only">Not available yet: </span>
        </>
    ) : incomplete ? (
        <>
            <span className="flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden="true">
                <span
                    className="h-3.5 w-3.5 rounded-full border-[1.5px] border-amber-500"
                    style={{ background: "conic-gradient(#F59E0B 0 50%, transparent 50% 100%)" }}
                />
            </span>
            <span className="sr-only">Saved, not finished: </span>
        </>
    ) : completed ? (
        <>
            <MdOutlineCheckCircle className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
            <span className="sr-only">Completed: </span>
        </>
    ) : (
        <>
            <span className="flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden="true"><span className="h-3.5 w-3.5 rounded-full border border-dashed border-slate-400" /></span>
            <span className="sr-only">Not completed: </span>
        </>
    );

const SidebarSubmenu: React.FC<SidebarSubmenuProps> = ({ items, currentStep, onClose }) => (
    <ul className="mt-1 space-y-1 pb-1 pl-2" aria-label="Steps">
        {items.map(step =>
        {
            const isCurrentStep = step.current ?? (stepKeyOf(step.path) === currentStep);

            if (step.disabled)
            {
                return (
                    <li key={step.id} className="flex items-center gap-2">
                        <StepStatusIcon completed={step.completed} locked />
                        <span
                            aria-disabled="true"
                            className="flex-1 truncate rounded-md px-2 py-1 text-sm text-slate-700 opacity-50 cursor-not-allowed whitespace-nowrap"
                        >
                            {step.title}
                        </span>
                    </li>
                );
            }

            return (
                <li key={step.id} className="flex items-center gap-2">
                    <StepStatusIcon completed={step.completed} incomplete={step.incomplete} />
                    <Link
                        to={step.path}
                        onClick={onClose}
                        aria-current={isCurrentStep ? "step" : undefined}
                        className={`flex-1 truncate rounded-md px-2 py-1 text-sm whitespace-nowrap transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ecpi-blue/50 ${isCurrentStep
                            ? "bg-(--ecpi-blue-hex) text-white"
                            : "text-slate-700 hover:bg-white hover:ring-1 hover:ring-[#c3d3ea] active:bg-[#D3E0F2]"
                            }`}
                    >
                        {step.title}
                    </Link>
                </li>
            );
        })}
    </ul>
);

const SidebarEntry: React.FC<SidebarEntryProps> = ({
    item,
    onClose
}) =>
{
    const { pathname, search } = useLocation();

    const disabledClasses = "opacity-50 cursor-not-allowed bg-white/5";
    const lockedReasonId = `locked-reason-${item.path.replace(/[^a-zA-Z0-9]/g, "-")}`;

    const isActive =
        pathname.startsWith(item.path.split("?")[0]) &&
        item.path !== "/dashboard";

    const currentStep = new URLSearchParams(search).get("step") || "personal";

    // Open by default: an entry with substeps is only in the menu while the applicant is on its route.
    const [submenuOpen, setSubmenuOpen] = useState(true);

    const menuItemClasses =
        "menu-item w-full flex items-center whitespace-nowrap " +
        (isActive ? "active" : "");

    const iconClasses =
        "w-[20px] h-[20px] shrink-0 mr-3 transition-colors duration-200 " +
        (isActive ? "text-(--ecpi-blue-hex)" : "text-slate-950");

    const renderedIcon: React.ReactNode = item.icon
        ? React.createElement(item.icon, { className: iconClasses })
        : null;

    const asCard = !!item.submenu && item.enabled && submenuOpen;

    return (
        <li className={asCard ? "rounded-xl bg-[#E6EDF7] p-2" : undefined}>
            <div className={menuItemClasses}>
                {item.enabled ? (
                    <Link
                        to={item.path}
                        aria-current={isActive ? "page" : undefined}
                        className={`relative flex items-center w-full ${asCard ? "h-10" : "h-15"} px-3 rounded-xl transition-all duration-200 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ecpi-blue/50 ${asCard
                            ? "hover:bg-white/50 active:bg-white/70"
                            : isActive
                                ? "bg-[#0054c71a] active:bg-[#0054c72b]"
                                : "hover:bg-[#0054c712] active:bg-[#0054c72b]"
                            }`}
                        onClick={onClose}
                    >
                        {isActive && (
                            <span aria-hidden="true" className="absolute left-0 top-2.5 bottom-2.5 w-1 rounded-r bg-(--ecpi-blue-hex)" />
                        )}
                        {renderedIcon}
                        <span className="block flex-1 overflow-hidden truncate whitespace-nowrap">
                            {item.label}
                        </span>
                    </Link>
                ) : (
                    <div className="group relative w-full">
                        <div
                            className={`flex items-center w-full h-15 px-3 rounded-xl transition-all duration-200 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ecpi-blue/50 ${disabledClasses}`}
                            aria-disabled="true"
                            tabIndex={item.lockedReason ? 0 : undefined}
                            aria-describedby={item.lockedReason ? lockedReasonId : undefined}
                        >
                            {renderedIcon}
                            <span className="block flex-1 overflow-hidden truncate whitespace-nowrap">
                                {item.label}
                            </span>
                        </div>
                        {item.lockedReason && (
                            <span
                                id={lockedReasonId}
                                role="tooltip"
                                className="pointer-events-none absolute left-3 top-full z-50 mt-1 hidden max-w-[260px] rounded-md bg-slate-800 px-3 py-2 text-xs font-normal text-white shadow-lg group-hover:block group-focus-within:block"
                            >
                                {item.lockedReason}
                            </span>
                        )}
                    </div>
                )}

                {item.submenu && item.enabled && (
                    <button
                        type="button"
                        onClick={() => setSubmenuOpen(open => !open)}
                        className="shrink-0 p-2 ml-1 rounded-lg transition-all duration-200 hover:bg-[#0054c71a]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ecpi-blue/50"
                        aria-label={`${submenuOpen ? "Hide" : "Show"} ${item.label} steps`}
                        aria-expanded={submenuOpen}
                    >
                        {submenuOpen
                            ? <MdExpandMore className="h-4 w-4" />
                            : <MdChevronRight className="h-4 w-4" />}
                    </button>
                )}
            </div>

            {item.submenu && item.enabled && submenuOpen && (
                <SidebarSubmenu items={item.submenu} currentStep={currentStep} onClose={onClose} />
            )}
        </li>
    );
};

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
    isOpen = false,
    onClose,
    profileComplete,
}) =>
{
    const { listSections, SectionIcon, SectionId, godMode, getUser } = useGlobal();
    const { pathname, search } = useLocation();
    const enrollmentSteps = useEnrollmentSteps();

    // The same per-tile statuses the dashboard tiles use, so a sidebar item is gated by exactly what its tile shows:
    // a tile that is hidden has no item, a tile that is disabled has a greyed-out, non-link item. The sidebar is not
    // mounted on /dashboard itself, so these requests are not made twice. Re-read on every route change (pathname).
    const user = getUser();
    const tileStatuses = useDynamicTileStatuses(user, pathname);
    const enrollmentStatus = useEnrollmentInformationStatus(user, pathname);

    // Admissions interview completion is set server-side (admin side). On failure the flag stays
    // false, i.e. Enrollment Information stays hidden.
    const [interviewCompleted, setInterviewCompleted] = useState(false);

    useEffect(() =>
    {
        let cancelled = false;

        interviewsApi
            .getAdmissionsInterviewStatus()
            .then(res =>
            {
                if (!cancelled)
                {
                    // The endpoint returns only { status }; the type's interviewCompleted is not sent.
                    setInterviewCompleted(res.status === "Completed");
                }
            })
            .catch(() =>
            {
                /* keep Enrollment Information hidden on failure */
            });

        return () =>
        {
            cancelled = true;
        };
    }, [profileComplete]);

    // Personal Profile steps the applicant has saved (completed-page GUIDs), so the submenu can mark them done.
    // Read only while the submenu is on screen (the profile route) and again after each step change, since
    // finishing a step moves the applicant to the next one.
    const onProfileRoute = pathname.startsWith("/dashboard/profile");
    const [completedPageIds, setCompletedPageIds] = useState<Set<string>>(() => new Set());
    // Pages that hold saved data but are not complete (the student moved on without signing a transcript request): amber.
    const [incompletePageIds, setIncompletePageIds] = useState<Set<string>>(() => new Set());

    useEffect(() =>
    {
        if (!onProfileRoute) return;
        let cancelled = false;

        dataApi
            .getCompletedSteps()
            .then(ids =>
            {
                if (!cancelled) setCompletedPageIds(new Set((ids ?? []).map(id => String(id).toLowerCase())));
            })
            .catch(() =>
            {
                /* no check marks on failure */
            });

        dataApi
            .getIncompleteSteps()
            .then(ids =>
            {
                if (!cancelled) setIncompletePageIds(new Set((ids ?? []).map(id => String(id).toLowerCase())));
            })
            .catch(() =>
            {
                /* no amber marks on failure (an older server has no such endpoint) */
            });

        return () =>
        {
            cancelled = true;
        };
    }, [onProfileRoute, search]);

    // NOTE: `logout` was removed from this destructure along with the Sign Out button below.
    // To reinstate, add `logout` back here and uncomment the handleLogout handler and footer JSX.

    // const [isLoggingOut, setIsLoggingOut] = useState(false);

    // Build menu items from SectionRegistry via useGlobal, filtering out Dashboard
    const menuItems: MenuItem[] = useMemo(() =>
    {
        // Exclude Dashboard (has its own dedicated link). Personal Profile is normally excluded too
        // (still navigable via the Dashboard tile and the My Profile menu in DashboardLayout), but is
        // shown in the side menu while the applicant is on the Personal Profile route, whether or not it is complete.
        const personalProfile = listSections().find(s => s.id === SectionId.PersonalProfile);
        const showPersonalProfile =
            personalProfile !== undefined &&
            personalProfile.hidden === false &&
            !!personalProfile.route &&
            // route carries a query string ("?step=personal") that pathname never has
            pathname.startsWith(personalProfile.route.split("?")[0]);

        // Enrollment Information is triggered from the admin side once the admissions interview
        // is completed, so it stays out of the side menu until the server reports that -- except while
        // the applicant is already on the enrollment page (reachable from the Dashboard tile or a link).
        // Hiding the menu from the page they are on leaves it with no way to see or move between its
        // steps, so the item (and its steps) is shown there, the same "shown on its route" rule that
        // Personal Profile uses above.
        const enrollmentSection = listSections().find(s => s.id === SectionId.EnrollmentInformation);
        const onEnrollmentRoute =
            !!enrollmentSection?.route &&
            pathname.startsWith(enrollmentSection.route.split("?")[0]);
        const showEnrollmentInformation = interviewCompleted || godMode || onEnrollmentRoute;

        let sections = listSections().filter(s =>
            s.id !== SectionId.Dashboard &&
            (s.id !== SectionId.PersonalProfile || showPersonalProfile) &&
            (s.id !== SectionId.EnrollmentInformation || showEnrollmentInformation) &&
            s.hidden === false &&
            // A tile hidden on the dashboard (Housing not required, no military affiliation, ...) has no item either.
            tileStatuses[s.id]?.hidden !== true);

        // Before Personal Profile is complete, only Admissions Interview is reachable --
        // DashboardLayout force-redirects every other route back to /dashboard/profile, and
        // Enrollment Information's own registry entry says "Access after scheduling your
        // admissions interview". This used to be `menuItems.slice(0, 2)`, i.e. "whichever two
        // sections happen to sort first" rather than naming the one that's actually meant to be
        // visible -- and because Enrollment Information shares SectionRegistry's order:30 with
        // Admissions Interview, it rode along as a clickable-looking nav item the applicant
        // couldn't actually reach yet.
        if (!profileComplete && !godMode) {
            sections = sections.filter(s =>
                s.id === SectionId.AdmissionsInterview || s.id === SectionId.PersonalProfile);
        }

        // Mirrors the dashboard: a tile's dynamic `disabled` (e.g. Financial Assistance "Completed;Locked") wins over the
        // registry's enabled flag, and Enrollment Information is unavailable until the applicant has an enrollment --
        // except on its own route, where the applicant already is (the status is still loading, or was just created).
        const isItemEnabled = (s: { id: string; enabled?: boolean }): boolean =>
        {
            if (s.id === SectionId.EnrollmentInformation && enrollmentStatus === "notStarted" && !onEnrollmentRoute)
            {
                return false;
            }
            return tileStatuses[s.id]?.disabled ?? (s.enabled !== false);
        };

        return sections.map(s => {
            const iconComp = (SectionIcon as any)[s.id] ?? null;
            return {
                path: s.route ?? "/",
                label: s.title,
                icon: iconComp,
                // Substeps are shown for Personal Profile and Enrollment Information only (the stakeholder UI
                // update removed them elsewhere). Enrollment Information's list comes from the page itself (it
                // depends on the applicant's answers), so it appears only while that page is open.
                submenu: s.id === SectionId.PersonalProfile
                    ? s.steps?.map(st =>
                    {
                        // The step's ?step= value is its PAGE_IDS key (registry ids are not always the URL values).
                        const pageId = PAGE_IDS[stepKeyOf(st.path) ?? ""];
                        return {
                            id: st.id,
                            title: st.title,
                            path: st.path,
                            completed: !!pageId && completedPageIds.has(pageId.toLowerCase()),
                            incomplete: !!pageId && !completedPageIds.has(pageId.toLowerCase()) && incompletePageIds.has(pageId.toLowerCase()),
                        };
                    })
                    : s.id === SectionId.EnrollmentInformation && enrollmentSteps.length > 0
                        ? enrollmentSteps.map(st => ({
                            id: st.key,
                            title: st.title,
                            path: `${(s.route ?? "/dashboard/program-enrollment").split("?")[0]}?step=${st.key}`,
                            current: st.current,
                            disabled: !st.reachable,
                        }))
                        : undefined,
                enabled: godMode ? true : isItemEnabled(s),
                lockedReason: s.dbBlurb || undefined
            } as MenuItem;
        });
    }, [listSections, SectionIcon, SectionId, godMode, profileComplete, pathname, interviewCompleted, enrollmentSteps, tileStatuses, enrollmentStatus, completedPageIds, incompletePageIds]);

    const displayMenuItems: MenuItem[] = menuItems;

    const renderedMenuItems = useMemo(() => displayMenuItems.map((item) =>
        (
            <SidebarEntry
            item={item}
            onClose={onClose}
            key={item.path}
        />)
        ),
        [displayMenuItems, onClose]);

    /* Temporarily removed with the Sign Out button below. Uncomment to reinstate.
    const handleLogout = async () => {
        if (isLoggingOut)
        {
            return; // Prevent multiple clicks
        }

        setIsLoggingOut(true);

        try
        {
            await logout();
        }
        catch (error)
        {
            console.error("Error during logout:", error);
        }
        finally
        {
            setIsLoggingOut(false);
        }
    };
    */

    const sidebarClasses = `
        fixed top-0 left-0 z-50 h-full w-[302px] backdrop-blur-md bg-ecpi-neutral bg-ecpi-neutral-text border-r border-slate-800/50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0 lg:static lg:z-auto lg:h-full
        flex flex-col overflow-y-auto shadow-2xl
    `;

    return (
        <aside className={sidebarClasses}>
            <div className="border-slate-800/50 flex items-center justify-between border-b p-4 lg:hidden">
                <h2 className="text-lg font-semibold">Navigation</h2>

                {/* Mobile Close Button */}
                <button
                    type="button"
                    className="rounded-lg p-2 transition-all duration-200 hover:bg-[#0054c71a]/40"
                    onClick={onClose}
                    aria-label="Close sidebar"
                >
                    <svg
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            </div>
            {/* Navigation */}
            <nav className="w-[270px] flex-1 justify-items-start">
                <ul className="ml-[16px] mr-[16px] mt-8 w-[270px] flex flex-col gap-2">
                    {profileComplete &&
                        //This doesn't need the active/inactive logic because the dashboard has no sidebar.
                        // The list's mt-8 on top of the logo's mb-4 = 48px from the logo to this button (on the list, not
                        // here, so the first item keeps that gap while this link is hidden before the profile is complete).
                        <li className="ecpi-db-container">
                            <Link
                                to="/dashboard"
                                className="flex items-center h-full px-3 rounded-xl transition-all duration-200 flex-1 whitespace-nowrap hover:bg-[#0054c71a]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ecpi-blue/50"
                                onClick={onClose}
                            >
                                <Home3 className="w-[20px] h-[20px] shrink-0 text-slate-950 mr-3" />
                                <span className="ecpi-sora-typog block flex-1 overflow-hidden truncate whitespace-nowrap">
                                    Dashboard
                                </span>
                            </Link>
                        </li>
                    }

                    {renderedMenuItems}
                </ul>
            </nav>

            {/* Footer - Sign Out button temporarily removed for MVP. The divider (border-t)
                is part of this footer block; uncomment the whole block to reinstate both.
                This space is reserved for future sidebar content.
            <div className="border-slate-800/50 border-t p-3">
                <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className={`
                        flex items-center w-full text-left rounded-xl transition-all duration-200 font-medium whitespace-nowrap
                        ${
                            isLoggingOut
                                ? "opacity-60 cursor-wait bg-white/5"
                                : "hover:bg-red-500/20 hover:border-red-400/30 cursor-pointer"
                        }
                    `}
                >
                    <MdLogout className="mr-3 h-5 w-5" />
                    <span>{isLoggingOut ? "Signing Out..." : "Sign Out"}</span>
                    {isLoggingOut && (
                        <div className="border-white/30 border-t-white ml-auto h-4 w-4 animate-spin rounded-full border-2"></div>
                    )}
                </button>
            </div>
            */}
        </aside>
    );
};

export default DashboardSidebar;
